import * as Sentry from '@sentry/react';
import type { Query } from '@tanstack/react-query';
import { COLLECTION_SCORING_WINDOW_MS, pollWhilePending } from '@vizit/api';
import type { GetCollectionScoringQuery, GetCollectionScoringQueryVariables } from '@vizit/api';
import type { CollectionScoring, ListCollectionScoringsQueryVariables } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';

type CollectionScoringQuery = Query<GetCollectionScoringQuery, unknown, GetCollectionScoringQuery>;

type CollectionScoringsQuery = Query<CollectionScoring[], unknown, CollectionScoring[]>;

/**
 * An upload into a collection that is already scored: when it was submitted (client clock, only to bound the wait)
 * and the newest `requestedAt` among the scorings at that moment (server clock), so the API's answer is told apart
 * by comparing server times only.
 */
interface CollectionUploadRescore {
  submittedAt: string;
  baselineRequestedAt: string | null;
}

/**
 * One collection scoring's status route, polled while it is `pending` (anchored on when it was requested) and left
 * alone once it is `ready` or `failed`. The API has no push channel, so this is how the category picker row flips
 * and how the page learns to re-read the collection's images for that pair.
 */
function collectionScoringStatusRefetchInterval(variables: GetCollectionScoringQueryVariables) {
  return pollWhilePending<CollectionScoringQuery>(
    (query) => (query.state.data?.status === 'pending' ? [query.state.data.requestedAt] : []),
    {
      staleTimeoutMs: COLLECTION_SCORING_WINDOW_MS,
      onTimeout: () => {
        Sentry.captureException(new Error('Collection scoring still pending after the stale window.'), {
          level: 'warning',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Collection,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Collection].collectionScoringPolling,
          },
          extra: { ...variables },
        });
      },
    },
  );
}

/** The scorings requested again since the upload, i.e. the API's answer to it. */
function rescoredSinceUpload(scorings: CollectionScoring[] | undefined, upload: CollectionUploadRescore) {
  const baselineMs = upload.baselineRequestedAt ? Date.parse(upload.baselineRequestedAt) : Number.NEGATIVE_INFINITY;
  return (scorings ?? []).filter((scoring) => Date.parse(scoring.requestedAt) > baselineMs);
}

/**
 * Whether the list still has to show the API's answer to an upload: the API scores new images under every category
 * the collection is scored under by requesting those scorings again. There is nothing to wait for while no scoring is
 * settled (a pending one takes the new images into its own run), and nothing once one was requested again.
 */
function awaitsRescoreAfterUpload(scorings: CollectionScoring[] | undefined, upload: CollectionUploadRescore) {
  return (
    !!scorings?.some((scoring) => scoring.status !== 'pending') && rescoredSinceUpload(scorings, upload).length === 0
  );
}

/**
 * The scorings list polls in one case only: after an upload, until the API has requested the collection's scorings
 * again for the new images. For an image uploaded without a product category it does that from the storage event,
 * seconds after the file is in S3; a categorized image is first scored under its own category, and only then are the
 * other pairs requested again (its own pair is not: the upload already scored those audiences). Either way a single
 * read right after the upload would come too early. Anchored on the submit time and left alone after the scoring
 * window; once a scoring shows as requested again, its status poll takes over.
 */
function collectionScoringsRefetchInterval(
  variables: ListCollectionScoringsQueryVariables,
  upload: CollectionUploadRescore | null,
) {
  return pollWhilePending<CollectionScoringsQuery>(
    (query) => (upload && awaitsRescoreAfterUpload(query.state.data, upload) ? [upload.submittedAt] : []),
    {
      staleTimeoutMs: COLLECTION_SCORING_WINDOW_MS,
      onTimeout: () => {
        Sentry.captureException(
          new Error('Collection scorings not requested again within the stale window after an upload.'),
          {
            level: 'warning',
            tags: {
              feature: SENTRY_FEATURE_TAGS.Collection,
              stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Collection].collectionScoringPolling,
            },
            extra: { ...variables, ...upload },
          },
        );
      },
    },
  );
}

export {
  awaitsRescoreAfterUpload,
  collectionScoringStatusRefetchInterval,
  collectionScoringsRefetchInterval,
  rescoredSinceUpload,
};
export type { CollectionUploadRescore };
